/**
 * NOLAS 2026, © Mika (Mikaël FOURNY). Fork TNE.
 * src/hooks/useSiteContent.ts
 *
 * Rôle :
 *   LOT-H (M7/H) : Hook de lecture du CONTENU ÉDITORIAL public (clés
 *   `content.*` éditées au back-office via /admin/content). Réutilise
 *   l'endpoint EXISTANT `GET /api/settings/public.php` (non modifié, les
 *   clés content.* sont is_public=1) et expose un accesseur `get(key)` qui
 *   renvoie la valeur configurée OU `null` si la clé est absente / vide.
 *
 *   ZÉRO BLOCAGE / offline-first : on partage la MÊME query TanStack que
 *   useSiteSettings (queryKey ['settings','public']) → aucun fetch
 *   supplémentaire. Si l'endpoint est absent / offline / lent, `get()`
 *   renvoie `null` → le consommateur (<ContentBlock>) retombe sur sa clé
 *   i18n (jamais de page vide, zéro régression).
 *
 * Réutilisable : 100 %, toute app DRANE avec `settings/public.php` et des
 *   clés de contenu préfixées peut le reprendre (changer le préfixe).
 *
 * Voir : api/settings/public.php (lecture), api/admin/site-content.php
 *        (écriture), components/content/ContentBlock.tsx (consommateur),
 *        hooks/useSiteSettings.ts (modèle, même query partagée).
 *
 * @author Mika
 * @since  2026-06-16 (LOT-H, contenu éditorial configurable)
 */

import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/api/client';

/** Préfixe des clés de contenu éditorial (périmètre borné LOT-H). */
export const CONTENT_KEY_PREFIX = 'content.';

/** Réponse brute de `settings/public.php` (valeurs = strings). */
interface PublicSettingsResponse {
  readonly ok: true;
  readonly settings: Readonly<Record<string, string>>;
}

/** Forme exposée aux consommateurs. */
export interface SiteContent {
  /**
   * Renvoie la valeur configurée pour `key` (ex. 'content.home.tagline'),
   * `trim`-ée et NON vide, sinon `null` (le consommateur retombe alors
   * sur sa clé i18n de repli).
   */
  readonly get: (key: string) => string | null;
  readonly isLoading: boolean;
}

/**
 * Clé de query PARTAGÉE avec useSiteSettings → même cache, aucun fetch
 * réseau dupliqué (les deux hooks lisent settings/public.php).
 */
const SITE_SETTINGS_QUERY_KEY = ['settings', 'public'] as const;

/**
 * Lit le contenu éditorial public et expose un accesseur tolérant.
 * Jamais d'exception : toute erreur réseau → `get()` renvoie `null`.
 */
export function useSiteContent(): SiteContent {
  const query = useQuery<PublicSettingsResponse, Error>({
    queryKey: SITE_SETTINGS_QUERY_KEY,
    queryFn: () => apiRequest<PublicSettingsResponse>('settings/public.php'),
    // Le contenu éditorial bouge peu, 60 s de fraîcheur, 10 min en cache.
    staleTime: 60_000,
    gcTime: 600_000,
    // 1 retry suffit ; au-delà on retombe sur les fallbacks i18n.
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const settings = query.data?.settings ?? {};

  const get = (key: string): string | null => {
    const raw = settings[key];
    if (typeof raw !== 'string') return null;
    const trimmed = raw.trim();
    return trimmed.length > 0 ? trimmed : null;
  };

  return { get, isLoading: query.isLoading };
}
